import React, { forwardRef, useImperativeHandle, useRef, useState, useCallback } from 'react'
import { GoogleMap } from '@react-google-maps/api'
import BaiduMap, { BaiduMapRef } from '@/component/map/baidu/BaiduMap'
import { useMapType } from '@/component/map/MapContext'
import MapFallback from '@/component/map/MapFallback'
import { wgs84ToBd09 } from '@/util/coordTransform'

export interface UnifiedMapProps {
    center: { lat: number; lng: number }
    zoom?: number
    mapContainerStyle?: React.CSSProperties
    className?: string
    options?: any
    onLoad?: (map: any) => void
    onZoomChanged?: () => void
    onCenterChanged?: () => void
    onIdle?: () => void
    children?: React.ReactNode
}

export interface UnifiedMapRef {
    panTo: (lat: number, lng: number) => void
    setZoom: (zoom: number) => void
    getZoom: () => number
    getCenter: () => { lat: () => number; lng: () => number } | null
    getBounds: () => any | null
    getMap: () => any
}

const DEFAULT_MAP_OPTIONS = {
    mapTypeControl: false
}

const UnifiedMap = forwardRef<UnifiedMapRef, UnifiedMapProps>(({
    center,
    zoom = 15,
    mapContainerStyle,
    className,
    options,
    onLoad,
    onZoomChanged,
    onCenterChanged,
    onIdle,
    children
}, ref) => {
    const mergedOptions = { ...DEFAULT_MAP_OPTIONS, ...options }
    const { mapType, failedMapType } = useMapType()
    const [googleMap, setGoogleMap] = useState<any>(null)
    const baiduMapRef = useRef<BaiduMapRef>(null)
    const baiduMapInstance = useRef<any>(null)

    useImperativeHandle(ref, () => ({
        panTo(lat: number, lng: number) {
            if (mapType === 'google' && googleMap) {
                googleMap.panTo({ lat, lng })
            } else if (mapType === 'baidu' && baiduMapInstance.current) {
                const [bdLat, bdLng] = wgs84ToBd09(lat, lng)
                const point = new window.BMapGL.Point(bdLng, bdLat)
                baiduMapInstance.current.panTo(point)
            }
        },
        setZoom(z: number) {
            if (mapType === 'google' && googleMap) {
                googleMap.setZoom(z)
            } else if (mapType === 'baidu' && baiduMapInstance.current) {
                baiduMapInstance.current.setZoom(z)
            }
        },
        getZoom() {
            if (mapType === 'google' && googleMap) {
                return googleMap.getZoom()
            } else if (mapType === 'baidu' && baiduMapInstance.current) {
                return baiduMapInstance.current.getZoom()
            }
            return zoom
        },
        getCenter() {
            if (mapType === 'google' && googleMap) {
                return googleMap.getCenter()
            }
            return null
        },
        getBounds() {
            if (mapType === 'google' && googleMap) {
                return googleMap.getBounds()
            } else if (mapType === 'baidu' && baiduMapInstance.current) {
                return baiduMapInstance.current.getBounds()
            }
            return null
        },
        getMap() {
            if (mapType === 'google') return googleMap
            return baiduMapInstance.current
        }
    }))

    const handleGoogleLoad = useCallback((mapInstance: any) => {
        setGoogleMap(mapInstance)
        onLoad?.(mapInstance)
    }, [onLoad])

    const handleBaiduLoad = useCallback((mapInstance: any) => {
        baiduMapInstance.current = mapInstance
        onLoad?.(mapInstance)
    }, [onLoad])

    if (failedMapType === mapType) {
        return <MapFallback className={className} style={mapContainerStyle || { width: '100%', height: '100%' }} />
    }

    if (mapType === 'baidu') {
        // 좌표 변환: WGS-84 -> BD-09
        const [bdLat, bdLng] = wgs84ToBd09(center.lat, center.lng)

        return (
            <BaiduMap
                ref={baiduMapRef}
                center={{ lat: bdLat, lng: bdLng }}
                zoom={zoom}
                className={className}
                style={mapContainerStyle}
                options={mergedOptions}
                onLoad={handleBaiduLoad}
                onZoomChanged={onZoomChanged}
                onCenterChanged={onCenterChanged}
                onIdle={onIdle}
            >
                {children}
            </BaiduMap>
        )
    }

    return (
        <GoogleMap
            onLoad={handleGoogleLoad}
            center={center}
            zoom={zoom}
            mapContainerStyle={mapContainerStyle || { width: '100%', height: '100%' }}
            options={mergedOptions}
            onZoomChanged={onZoomChanged}
            onCenterChanged={onCenterChanged}
            onIdle={onIdle}
        >
            {children}
        </GoogleMap>
    )
})

export default UnifiedMap
